import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Divider,
} from "@mui/material";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import GavelIcon from "@mui/icons-material/Gavel";
import SecurityIcon from "@mui/icons-material/Security";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForward";
import LaunchIcon from "@mui/icons-material/Launch";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import InfoBox from "../utils/InfoBox";
import { getImageUrl } from "../../../utils/helpers";

const KthAmazonPatentEnforcement = ({ baseConfig, sectionStyles }) => {
  const {
    priColor,
    priHoverColor,
    tertColor,
    tertHoverColor,
    secColor,
    quadColor,
    quadHoverColor,
    linkColor,
  } = baseConfig.siteBranding;

  return (
    <Card sx={{ ...sectionStyles.card, mb: 2 }}>
      <Box
        sx={{
          p: 2,
          display: "flex",
          alignItems: "center",
          backgroundColor: `#FF990022`,
        }}
      >
        <ShoppingCartIcon sx={{ mr: 2, color: "#FF9900" }} />
        <Typography variant="h6" fontWeight="bold">
          Amazon Patent Enforcement
        </Typography>
      </Box>
      <CardContent sx={{ p: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card
              variant="outlined"
              sx={{
                borderLeft: `6px solid #FF9900`,
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <CardContent sx={{ p: 0, flexGrow: 1 }}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  sx={{
                    backgroundColor: "#fff",
                    p: 1,
                    borderBottom: "1px solid #eee",
                  }}
                >
                  <img
                    src={getImageUrl("amz-pat-disputes.png")}
                    alt="Amazon Patent Disputes"
                    style={{
                      width: "180px",
                      height: "auto",
                      marginRight: 10,
                    }}
                  />
                  <Typography variant="h6" fontWeight="bold">
                    Patent Enforcement
                  </Typography>
                </Box>

                <Box sx={{ backgroundColor: "#f8f9fa", p: 1 }}>
                  <Grid container>
                    <Grid item xs={12} sm={6}>
                      <Box display="flex">
                        <GavelIcon sx={{ color: "#28a745", mr: 1 }} />
                        <Typography variant="subtitle1" fontWeight="bold">
                          For Patent Owners
                        </Typography>
                      </Box>
                      <List dense>
                        <ListItem>
                          <ListItemIcon>
                            <ChevronRightIcon sx={{ color: "#28a745" }} />
                          </ListItemIcon>
                          <ListItemText primary="Enforce patents against unauthorized sellers" />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon>
                            <ChevronRightIcon sx={{ color: "#28a745" }} />
                          </ListItemIcon>
                          <ListItemText primary="Navigate the APEX program effectively" />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon>
                            <ChevronRightIcon sx={{ color: "#28a745" }} />
                          </ListItemIcon>
                          <ListItemText primary="Strategic claim mapping assistance" />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon>
                            <ChevronRightIcon sx={{ color: "#28a745" }} />
                          </ListItemIcon>
                          <ListItemText primary="Settlement negotiation" />
                        </ListItem>
                      </List>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Box display="flex">
                        <SecurityIcon sx={{ color: "#FF9900", mr: 1 }} />
                        <Typography variant="subtitle1" fontWeight="bold">
                          For Amazon Sellers
                        </Typography>
                      </Box>
                      <List dense>
                        <ListItem>
                          <ListItemIcon>
                            <ChevronRightIcon sx={{ color: "#FF9900" }} />
                          </ListItemIcon>
                          <ListItemText primary="Defend against infringement claims" />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon>
                            <ChevronRightIcon sx={{ color: "#FF9900" }} />
                          </ListItemIcon>
                          <ListItemText primary="Reinstate suspended ASINs" />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon>
                            <ChevronRightIcon sx={{ color: "#FF9900" }} />
                          </ListItemIcon>
                          <ListItemText primary="Non-infringement analysis" />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon>
                            <ChevronRightIcon sx={{ color: "#FF9900" }} />
                          </ListItemIcon>
                          <ListItemText primary="Product redesign consultation" />
                        </ListItem>
                      </List>
                    </Grid>
                  </Grid>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="body1" paragraph sx={{ ml: 4 }}>
              Amazon's marketplace presents unique opportunities and challenges
              for patent enforcement. Our specialized expertise helps patent
              owners protect their intellectual property rights against
              unauthorized Amazon sellers, as well as defends Amazon sellers
              against patent infringement allegations.
            </Typography>

            <Typography variant="body1" paragraph sx={{ ml: 4 }}>
              We provide comprehensive guidance for navigating Amazon's Patent
              Evaluation Express (APEX) program, which offers a streamlined
              alternative to traditional litigation for resolving patent
              disputes involving Amazon sellers. Whether you're asserting your
              patent rights or defending against claims, our team delivers
              effective strategies tailored to Amazon's specific enforcement
              environment.
            </Typography>

            <Box p={2}>
              <Button
                variant="contained"
                sx={{
                  ml: 2,
                  backgroundColor: tertColor,
                  color: "#fff",
                  "&:hover": {
                    backgroundColor: tertHoverColor,
                    color: "#fff",
                  },
                }}
                component={RouterLink}
                to="/amazon-patent-enforcement"
                endIcon={<LaunchIcon />}
              >
                Learn More
              </Button>
            </Box>
          </Grid>

          {/* FULL WIDTH: Wizard Box */}
          <Grid item xs={12}>
            <Box
              sx={{
                p: 2,
                mt: 2,
                backgroundColor: "#fff8e1",
                borderRadius: 2,
                border: "1px solid #ffe082",
              }}
            >
              <Typography
                variant="subtitle1"
                fontWeight="bold"
                gutterBottom
                color="#ff8f00"
              >
                Patent Enforcement Wizard
              </Typography>
              <Typography variant="body2">
                Explore how our Patent Enforcement Wizard can provide valuable
                insights. Whether you're an Amazon seller or a patent owner, the
                wizard simplifies the enforcement or defense flow and offers
                actionable guidance to help resolve your dispute effectively.
              </Typography>
              <Button
                component={RouterLink}
                to="/patent-enforcement"
                sx={{ mt: 1, color: "#ff8f00" }}
                endIcon={<LaunchIcon />}
              >
                Try Our Patent Enforcement Wizard
              </Button>
            </Box>
          </Grid>
        </Grid>

        {/* APEX Program Overview */}
        <Box sx={{ mt: 4 }}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              color: "#FF9900",
              borderBottom: `1px solid #FF990033`,
              pb: 1,
            }}
          >
            Amazon's APEX Program: A Streamlined Dispute Resolution Process
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={7}>
              <Typography variant="body1" paragraph>
                Amazon's Patent Evaluation Express (APEX) program offers a
                faster and more cost-effective alternative to traditional
                litigation for patent disputes involving Amazon sellers. The
                program enables patent owners to have their infringement claims
                evaluated by a neutral third-party attorney, potentially
                resulting in the removal of infringing listings from Amazon's
                marketplace.
              </Typography>

              <Typography variant="body1" paragraph>
                While APEX simplifies the enforcement process, it also presents
                unique challenges and strategic considerations for both patent
                owners and Amazon sellers. Our firm's expertise in this
                specialized process ensures you navigate APEX effectively,
                whether you're asserting your patent rights or defending your
                product listings.
              </Typography>

              <Box
                sx={{
                  p: 2,
                  backgroundColor: "#f5f5f5",
                  borderRadius: 2,
                  border: `1px solid #FF990033`,
                  mt: 2,
                }}
              >
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Key APEX Considerations:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <ChevronRightIcon sx={{ color: "#FF9900" }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Streamlined process focused solely on non-infringement"
                      secondary="Patent validity challenges are not considered in APEX proceedings"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <ChevronRightIcon sx={{ color: "#FF9900" }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Fixed fee structure"
                      secondary="Lower cost than litigation, with the loser paying the neutral evaluator's fee"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <ChevronRightIcon sx={{ color: "#FF9900" }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Binding decision by a patent attorney evaluator"
                      secondary="Rapid resolution compared to court proceedings"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <ChevronRightIcon sx={{ color: "#FF9900" }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Strategic implications beyond Amazon"
                      secondary="Decisions may impact broader enforcement or defense strategies"
                    />
                  </ListItem>
                </List>
              </Box>
            </Grid>

            <Grid item xs={12} md={5}>
              <Box
                sx={{
                  p: 3,
                  backgroundColor: "#fff",
                  borderRadius: 2,
                  border: "1px solid #e0e0e0",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                }}
              >
                <Typography
                  variant="h6"
                  gutterBottom
                  fontWeight="bold"
                  color="#FF9900"
                >
                  Why Choose Our Amazon Patent Services?
                </Typography>

                <List>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon sx={{ color: "#28a745" }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Technical Expertise in Engineering & Technology"
                      secondary="We combine legal knowledge with deep technical understanding"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon sx={{ color: "#28a745" }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Extensive Experience with Amazon's Processes"
                      secondary="Proven track record in APEX proceedings and Amazon brand protection"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon sx={{ color: "#28a745" }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Strategic Approach"
                      secondary="We consider both immediate resolution and long-term business objectives"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon sx={{ color: "#28a745" }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Comprehensive IP Support"
                      secondary="Patent, trademark, and copyright expertise for complete brand protection"
                    />
                  </ListItem>
                </List>

                <Box mt={2} textAlign="center">
                  <Button
                    component={RouterLink}
                    to="/amazon-patent-enforcement"
                    variant="contained"
                    sx={{
                      backgroundColor: "#FF9900",
                      color: "white",
                      "&:hover": {
                        color: "white",

                        backgroundColor: "#e68a00",
                      },
                    }}
                    endIcon={<ArrowForwardIosIcon />}
                  >
                    Amazon Patent Services
                  </Button>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Success Story */}
        <InfoBox
          title="Success Story: Amazon Seller ASIN Reinstatement"
          content={
            <Typography variant="body2">
              We recently helped a technology hardware company defend against a
              patent infringement claim that had resulted in the suspension of
              their best-selling product on Amazon. By conducting a detailed
              non-infringement analysis and effectively presenting our arguments
              through the APEX program, we successfully demonstrated that our
              client's product did not infringe the asserted patent claims.
              Amazon reinstated the product listing within weeks, allowing our
              client to resume sales of a product that represented over 30% of
              their annual revenue.
            </Typography>
          }
          type="success"
        />
      </CardContent>
    </Card>
  );
};

export default KthAmazonPatentEnforcement;
